import { alpha, Box, Card, CardContent, Collapse, Typography, useTheme } from "@mui/material";
import ChipSingleSelect from "../../../../components/ChipSingleSelect";
import { formatMinutesToHours } from "src/utils/reservationFunctions";
import { RESERVATION_CONSTANTS } from "../../../../config/constants/reservation-constants";
import { BookingAccordionNoOptions } from "../listing-details/BookingAccordionNoOptions";
import { useLocales } from "../../../../locales";
import React, { useEffect, useMemo, useState } from "react";
import { getBookingDays } from "./BookingDateUtils";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { roRO } from '@mui/x-date-pickers/locales';
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import 'dayjs/locale/ro';
import GuestsIcon from 'src/assets/icons/booking/GuestsIcon';
import LengthIcon from 'src/assets/icons/booking/LengthIcon';
import HoldingFeeIcon from 'src/assets/icons/booking/HoldingFeeIcon';
import { LocationIcon } from "src/assets/icons/LocationIcon";
import { ClockIcon } from "src/assets/icons/ClockIcon";
import DateIcon from "src/assets/icons/DateIcon";
import Iconify from "src/components/iconify";
import Chip from "src/components/Chip";
import CollapsibleChipSelect from "src/components/CollapsibleChipSelect";
import ShineIcon from "src/assets/icons/booking/ShineIcon";
import RHFEditor from "src/components/hook-form/RHFEditor";
import { RHFTextField } from "src/components/hook-form";
import { useAuthContext } from "src/hooks/useAuthContext";
import FireIcon from "src/assets/icons/booking/FireIcon";
import { useNavigate } from "react-router";
import PhoneIcon from "src/assets/icons/PhoneIcon";
import MobileIconLarge from "src/assets/icons/floor-plan/MobileIconLarge";
import BellIcon from "src/assets/icons/BellIcon";
import BookingWaitlistCard from "./BookingWaitlistCard";


const BookingDataCard = ({
  eventGeneralData,
  watch,
  setValue,
  trigger,
  getHeader,
  errors,
  availableDates,
  availableLength,
  availableAreas,
  availableHours,
  specialOccasions,
  dietaryRestrictions,
  bookingCardVariant,
  loadingCheckAvailability,
  isInIframe,
  holdingFee,
  enabledWaitlist,
  loadAvailability,
  isBookableUnit,
  enabledSpecialRequirements,
  enabledCheckAvailability,
  hoursWithEventLabel,
  ongoingLinkedEvent,
  goToLinkedEvent,
  guestLimits
}) => {
  const { translate, currentLang } = useLocales();
  const theme = useTheme();
  const navigate = useNavigate();

  const { user, refresh } = useAuthContext();
  const [bookingDays, setBookingDays] = useState([]);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);

  const [openSpecialOccasions, setOpenSpecialOccasions] = useState(false);
  const [openUserDietaryRestrictions, setOpenUserDietaryRestrictions] = useState(false);
  const [openPartyDietaryRestrictions, setOpenPartyDietaryRestrictions] = useState(false);
  const [openDescription, setOpenDescripton] = useState(false);

  const selectedSpecialOccasions = watch("specialOccasions");
  const selectedUserDietaryRestrictions = watch("userDietaryRestrictions");
  const selectedPartyDietaryRestrictions = watch("partyDietaryRestrictions");
  const description = watch("description");

  const guests = Array.from(
    { length: guestLimits.maxGuests - guestLimits.minGuests + 1 },
    (_, i) => guestLimits.minGuests + i
  );

  const guestsW = watch("guests");
  const underMinBookingGuests = guestsW < guestLimits.minBookingGuests;
  const overMaxBookingGuests = guestsW > guestLimits.maxBookingGuests;

  const linkedEventDates = useMemo(() => {
    return new Set(
      (eventGeneralData?.linkedEvents ?? []).map(e =>
        dayjs(e.general.startDate).format('YYYY-MM-DD')
      )
    );
  }, [eventGeneralData?.linkedEvents]);

  const updateRequirementArray = (key, id) => {
    const requirementArray = watch(key);

    const index = requirementArray.findIndex((item) => item === id);

    if (index !== -1) {
      requirementArray.splice(index, 1);
    } else {
      requirementArray.push(id);
    }

    return requirementArray;
  };

  useEffect(() => {
    (async () => {
      const days = await getBookingDays(
        eventGeneralData,
        watch("date"),
        availableDates,
        currentLang.value
      );
      setBookingDays(days);
    })();
  }, [availableDates, watch("date")]);

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    const userDietaryRestrictions = user?.dietaryRestrictions?.map(it => it.id) ?? [];
    !selectedUserDietaryRestrictions?.length && setValue("userDietaryRestrictions", userDietaryRestrictions)
  }, [user]);

  return (
    <>
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {getHeader(
            translate("bookings.guests"),
            <GuestsIcon />,
            errors.guests
          )}
        </Box>
        <Box sx={{ marginLeft: 3.5 }}>
          <ChipSingleSelect
            singleRow={true}
            onChange={(val) => {
              setValue("guests", val);
              trigger("guests");
              loadAvailability("guests");
            }}
            items={guests}
            value={watch("guests")}
            rowOnSmScreen={true}
            isInIframe={isInIframe}
          />
        </Box>
      </>

      {underMinBookingGuests || overMaxBookingGuests ?
        <Card raised sx={{
          backgroundColor: theme.palette.background.neutral,
          mt: 4,
          borderRadius: "8px",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, paddingX: 1, paddingY: 1.5 }}>
            <Box>
              <MobileIconLarge />
            </Box>
            <Typography variant="body1">
              {overMaxBookingGuests
                ? `${translate("bookings.overMaxBookingGuests1")} ${guestLimits.maxBookingGuests} ${translate("bookings.overMaxBookingGuests2")} ${eventGeneralData?.organizerPhone}.`
                : `${translate("bookings.underMinBookingGuests1")} ${guestLimits.minBookingGuests} ${translate("bookings.underMinBookingGuests2")}`
              }
            </Typography>
          </Box>
        </Card>
        : <>
          {bookingCardVariant ===
            RESERVATION_CONSTANTS.BOOKING_CARD_VARIANTS.FULL && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {getHeader(
                    translate("bookings.date"),
                    <DateIcon />,
                    errors.date
                  )}
                </Box>
                <Box sx={{ marginLeft: 3.5 }}>
                  {bookingDays.length > 0 ? (
                    <ChipSingleSelect
                      singleRow={true}
                      onChange={(val) => {
                        if (val === 'OTHER') {
                          setIsCalendarVisible(true);
                        } else {
                          setIsCalendarVisible(false);
                          setValue("date", val);
                          trigger("date");
                          loadAvailability("date");
                        }
                      }}
                      items={bookingDays}
                      value={watch("date")}
                      rowOnSmScreen={true}
                      isInIframe={isInIframe}
                    />
                  ) : (
                    <BookingAccordionNoOptions
                      loadingCheckAvailability={loadingCheckAvailability}
                      isPermanentEvent={eventGeneralData?.isPermanent}
                    />
                  )}
                </Box>
                {isCalendarVisible && (
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={currentLang.value}
                    localeText={currentLang.value === "ro" && roRO.components.MuiLocalizationProvider.defaultProps.localeText}
                  >
                    <StaticDatePicker
                      onChange={(date) => {
                        setValue("date", dayjs(date).format('YYYY-MM-DD'));
                        trigger("date");
                        loadAvailability("date");
                        setIsCalendarVisible(false);
                      }}
                      minDate={dayjs().startOf('month')}
                      maxDate={dayjs().add(1, 'year').endOf('month')}
                      showToolbar={false}
                      shouldDisableDate={(date) => !availableDates.find(it => it.name === date.format(RESERVATION_CONSTANTS.YEAR_FORMAT))}
                      value={dayjs(watch("date"))}
                      name="date"
                      slotProps={{
                        DatePickerToolbar: { style: { display: "none" } },
                        nextIconButton: {
                          style: {
                            pointerEvents: loadingCheckAvailability ? "none" : "auto",
                          },
                        },
                        previousIconButton: {
                          style: {
                            pointerEvents: loadingCheckAvailability ? "none" : "auto",
                          },
                        },
                        actionBar: {
                          actions: ["cancel"],
                          onClick: () => setIsCalendarVisible(false),
                        },
                        day: (dayProps) => {
                          const isLinkedEvent = linkedEventDates.has(dayProps.day.format('YYYY-MM-DD'));
                          return {
                            style: {
                              fontWeight: "500",
                              ...(isLinkedEvent && {
                                outline: '2px solid #007AFF',
                                outlineOffset: '-2px',
                                borderRadius: '50%',
                              }),
                            },
                          };
                        },
                      }}
                      disablePast
                      disabled={loadingCheckAvailability}
                      sx={{
                        backgroundColor: "transparent",
                        "& .MuiPickersDay-root": {
                          "&.Mui-selected": {
                            color: theme.palette.background.default,
                            backgroundColor: theme.palette.text.primary,
                          },
                          "&.Mui-disabled:not(.Mui-selected)": {
                            color: `${theme.palette.text.secondary}`,
                          },
                          "&:hover:not(.Mui-selected)": {
                            backgroundColor: alpha(theme.palette.text.primary, 0.1),
                            color: theme.palette.text.primary,
                          },
                        },
                        "& .MuiButton-root": {
                          color: theme.palette.text.primary,
                        },
                        "& .MuiPickersCalendarHeader-labelContainer": {
                          pointerEvents: "none",
                        },
                        "& .MuiPickersCalendarHeader-switchViewButton": {
                          visibility: "hidden",
                        },
                        "& .MuiDialogActions-root": {
                          justifyContent: "center",
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              </>
            )}

          {bookingCardVariant ===
            RESERVATION_CONSTANTS.BOOKING_CARD_VARIANTS.FULL && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {getHeader(
                    translate("bookings.length"),
                    <LengthIcon />,
                    errors.length
                  )}
                </Box>
                <Box sx={{ marginLeft: 3.5 }}>
                  {availableLength.length > 0 ? (
                    <ChipSingleSelect
                      singleRow={true}
                      rowOnSmScreen={true}
                      onChange={(val) => {
                        setValue("length", val);
                        trigger("length");
                        loadAvailability("length");
                      }}
                      items={availableLength.map((val) => ({
                        label: formatMinutesToHours(val),
                        value: val,
                      }))}
                      value={watch("length")}
                      isInIframe={isInIframe}
                    />
                  ) : (
                    <BookingAccordionNoOptions
                      loadingCheckAvailability={loadingCheckAvailability}
                      isPermanentEvent={eventGeneralData?.isPermanent}
                    />
                  )}
                </Box>
              </>
            )}

          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {getHeader(
                translate(isBookableUnit ? "bookings.category" : "bookings.area"),
                <LocationIcon />,
                errors.area
              )}
            </Box>
            <Box sx={{ marginLeft: 3.5 }}>
              {availableAreas.length > 0 ? (
                <ChipSingleSelect
                  singleRow={true}
                  rowOnSmScreen={true}
                  onChange={(val) => {
                    setValue("area", val);
                    trigger("area");
                    loadAvailability("area");
                  }}
                  items={availableAreas}
                  value={watch("area")}
                  isInIframe={isInIframe}
                />
              ) : (
                <BookingAccordionNoOptions
                  loadingCheckAvailability={loadingCheckAvailability}
                  isPermanentEvent={eventGeneralData?.isPermanent}
                />
              )}
            </Box>
          </>

          {bookingCardVariant ===
            RESERVATION_CONSTANTS.BOOKING_CARD_VARIANTS.FULL && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {getHeader(translate("bookings.time"), <ClockIcon />, errors.time)}
                </Box>
                <Box sx={{ marginLeft: 3.5 }}>
                  {availableHours.length > 0 ? (
                    <ChipSingleSelect
                      singleRow={true}
                      rowOnSmScreen={true}
                      onChange={(val) => {
                        setValue("time", val);
                        trigger("time");
                        loadAvailability("time");
                      }}
                      items={hoursWithEventLabel}
                      value={watch("time")}
                      isInIframe={isInIframe}
                    />
                  ) : (
                    <BookingAccordionNoOptions
                      loadingCheckAvailability={loadingCheckAvailability}
                      isPermanentEvent={eventGeneralData?.isPermanent}
                    />
                  )}
                </Box>
              </>
            )}

          {ongoingLinkedEvent && (
            <>
              {getHeader(translate("bookings.ongoingEvent"), <FireIcon />, errors.time)}
              <Card raised onClick={goToLinkedEvent} sx={{
                backgroundColor: theme.palette.background.neutral,
                borderRadius: "8px",
                mt: 2,
                maxWidth: "268px",
                cursor: "pointer"
              }}>
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', py: '8px !important', px: '8px !important' }}>
                  {ongoingLinkedEvent.general.images?.[0] && (
                    <Box
                      component="img"
                      src={`${ongoingLinkedEvent.general.images[0]}@200`}
                      sx={{ width: 56, aspectRatio: '2/3', borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                    />
                  )}
                  <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, height: 84, justifyContent: "center", }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {ongoingLinkedEvent.general.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(ongoingLinkedEvent.general.startDate).format('D MMM | HH:mm')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </>

          )}

          {!!holdingFee && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                {getHeader(translate("bookings.holdingFee"), <HoldingFeeIcon />, errors.time)}
              </Box>
              <Card raised sx={{
                marginLeft: 3.5,
                backgroundColor: theme.palette.background.neutral,
                maxWidth: 453,
                borderRadius: "8px",
              }}>
                <CardContent>
                  <Typography variant="h4">
                    {holdingFee} {eventGeneralData?.currency}
                  </Typography>

                  <Typography variant="h6">
                    {translate("bookings.howItWorks")}
                  </Typography>
                  <Typography variant="body1">
                    {translate(isBookableUnit ? "bookings.holdingFeeDisclaimerShort" : "bookings.holdingFeeDisclaimer")}
                  </Typography>
                </CardContent>
              </Card>
            </>
          )}

          {enabledWaitlist && (
            <BookingWaitlistCard/>
          )}

          {enabledSpecialRequirements && (
            <>
              <Card raised sx={{
                backgroundColor: theme.palette.background.neutral,
                mt: 4,
                borderRadius: "8px",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, paddingX: 1, paddingY: 1.5 }}>
                  <Box>
                    <ShineIcon />
                  </Box>
                  <Typography variant="body1">
                    {translate("bookings.specialRequirements")}
                  </Typography>
                </Box>
              </Card>
              <CollapsibleChipSelect
                listKey={"specialOccasions"}
                title={translate("bookings.specialOccasionsTitle")}
                open={openSpecialOccasions}
                setOpen={setOpenSpecialOccasions}
                options={specialOccasions}
                selectedOptions={selectedSpecialOccasions}
                setValue={setValue}
                updateArray={updateRequirementArray}
              />
              <CollapsibleChipSelect
                listKey={"userDietaryRestrictions"}
                title={translate("bookings.userDietaryRestrictionsTitle")}
                open={openUserDietaryRestrictions}
                setOpen={setOpenUserDietaryRestrictions}
                options={dietaryRestrictions}
                selectedOptions={selectedUserDietaryRestrictions}
                setValue={setValue}
                updateArray={updateRequirementArray}
              />
              <CollapsibleChipSelect
                listKey={"partyDietaryRestrictions"}
                title={translate("bookings.partyDietaryRestrictionsTitle")}
                open={openPartyDietaryRestrictions}
                setOpen={setOpenPartyDietaryRestrictions}
                options={dietaryRestrictions}
                selectedOptions={selectedPartyDietaryRestrictions}
                setValue={setValue}
                updateArray={updateRequirementArray}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  cursor: "pointer"
                }}
                onClick={() => { setOpenDescripton(prev => !prev) }}
              >
                {getHeader(translate("bookings.anythingElseTitle"), <Iconify
                  icon="eva:chevron-down-fill"
                  width={32}
                  sx={{
                    display: "inline-flex",
                    transition: (t) =>
                      t.transitions.create("transform", {
                        duration: 300,
                        easing: t.transitions.easing.easeInOut,
                      }),
                    transform: openDescription ? "rotate(0deg)" : "rotate(-90deg)",
                    willChange: "transform",
                  }}
                  aria-hidden
                />)}
              </Box>
              <Collapse
                in={openDescription}
                timeout={300}
                collapsedSize={0}
                unmountOnExit={false}
              >
                <Box sx={{
                  marginLeft: 3.5,
                  px: 1,
                }}>
                  <RHFTextField
                    id="description"
                    name="description"
                    multiline
                    placeholder={translate("eventManager.writeSomethingAwesome")}
                    maxLength={500}
                    noNewline
                  />
                  <Typography variant="caption" color="grey" sx={{ ml: 0.5 }}>{`${description.length}/500 characters`}</Typography>
                </Box>
              </Collapse>

            </>
          )}
        </>
      }
    </>
  );
};


export default BookingDataCard;
